import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { visuallyHidden } from '@mui/utils';

import { IconBadge } from '@/components/atoms';
import { type IconKey } from '@/content/site';

type Props = {
  stage: { title: string; text: string; icon: IconKey };
  /** One-based, for the numeral beside the row. */
  position: number;
  last: boolean;
};

/**
 * One stage of the handover, read as a row on a timeline rather than a tile.
 *
 * The three stages happen in an order — package services, updates, handover —
 * and three tiles in a row said nothing about that order beyond reading
 * direction. The timeline is also the same arrangement at every width, so the
 * phone, where most of this traffic is, gets the designed layout rather than a
 * collapsed one.
 *
 * The connector is decorative and switched off on the last row; the `<ol>` and
 * the hidden "Stage n" prefix carry the sequence.
 */
export function HandoverStageRow({ stage, position, last }: Props) {
  return (
    <Box
      component="li"
      sx={{ display: 'flex', gap: { xs: 5, md: 7 }, pb: last ? 0 : { xs: 8, md: 10 } }}
    >
      <Box sx={{ position: 'relative', flexShrink: 0 }}>
        <IconBadge name={stage.icon} size={56} shape="circle" tone="tint" />
        {!last && (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              top: 64,
              bottom: { xs: -32, md: -40 },
              width: '2px',
              bgcolor: 'divider',
            }}
          />
        )}
      </Box>

      <Stack spacing={2} sx={{ pt: 1, flex: 1, minWidth: 0 }}>
        {/* The numeral is for the eye only: the heading below already carries
            it to a screen reader, and hearing it twice is noise. */}
        <Typography aria-hidden variant="overline" sx={{ color: 'primary.main' }}>
          Stage {position}
        </Typography>
        <Typography variant="h4" component="h3">
          <Box component="span" style={visuallyHidden}>
            Stage {position}:{' '}
          </Box>
          {stage.title}
        </Typography>
        <Typography color="text.secondary" sx={{ maxWidth: '52ch' }}>
          {stage.text}
        </Typography>
      </Stack>
    </Box>
  );
}
